import React, { useEffect, useState } from "react";
import { message, Modal } from "antd";
import { FilePdfOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import ComposeStyle from "./ComposeStyle";
import FetchData from "../../../hooks/FetchData";
import useMailbox from "./useMailbox";

// "04th Mar, 2024 ; 10:20 AM" - matches the Figma message view/reply header.
const formatMessageDateTime = (value) => {
  const date = dayjs(value);
  if (!date.isValid()) return "";
  const day = date.date();
  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
      ? "nd"
      : day % 10 === 3 && day !== 13
      ? "rd"
      : "th";
  return `${String(day).padStart(2, "0")}${suffix} ${date.format("MMM, YYYY ; hh:mm A")}`;
};

// Short label only ("pdf", "docx") - Figma shows just the file type
// under the icon. file_type is the upload's mime type ("application/pdf");
// fall back to the filename's extension if that's ever missing.
const fileTypeLabel = (file) => {
  if (file.file_type) {
    const subtype = file.file_type.split("/").pop();
    if (subtype) return subtype.toLowerCase();
  }
  const parts = (file.file_name || "").split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "file";
};

const ViewMessage = () => {
  const navigate = useNavigate();
  const { messageId } = useParams();
  const clientId = useSelector((state) => state.authTracking.userInfo?.clientId);
  const { isPersonView, peopleId, query } = useMailbox();
  const [msg, setMsg] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);

  useEffect(() => {
    if (!clientId || !messageId) return;
    FetchData({ endPoint: `/message/getmessage/${clientId}/${messageId}${query}` })
      .then((res) => setMsg(res?.response))
      .catch(() => message.error("Failed to load message"));
  }, [clientId, messageId, query]);

  // Sent vs Inbox comes from who sent it relative to the mailbox being
  // viewed: the Admin's own messages have sender_people_id NULL (see
  // create_messages.sql); in View As mode it's that person's own id.
  const isSent = isPersonView ? msg?.sender_people_id === peopleId : !msg?.sender_people_id;
  const listPath = isSent ? "/messages?tab=sent" : "/messages";
  const goBack = () => navigate(listPath);

  if (!msg) return null;

  const recipientLine = (msg.recipient_names || []).filter(Boolean).join(", ") || "-";

  return (
    <ComposeStyle>
      <p className="compose-breadcrumb">
        <span className="crumb-link" onClick={goBack}>
          {isSent ? "Sent" : "Inbox"}
        </span>
        {" > "}
        <span className="crumb-current">{msg.subject}</span>
      </p>

      <div className="original-message-block no-border">
        <div className="original-message-header">
          <div className="original-message-avatar">
            {(isSent && !isPersonView ? "A" : msg.sender_name || "?").charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="original-message-from">
              {isSent ? `To: ${recipientLine}` : `From: ${msg.sender_name}`}
            </p>
            <p className="original-message-date">{formatMessageDateTime(msg.created_at)}</p>
          </div>
        </div>
        <p className="original-message-subject">{msg.subject}</p>
        <div className="original-message-body" dangerouslySetInnerHTML={{ __html: msg.body }} />

        {msg.attachments?.length > 0 && (
          <div className="attachment-card-grid">
            {msg.attachments.map((file) => (
              <div
                className="attachment-card"
                key={file.attachment_id}
                role="button"
                tabIndex={0}
                onClick={() => setPreviewFile(file)}
              >
                <p className="attachment-card-title">{file.file_name}</p>
                <div className="attachment-card-file-row">
                  <FilePdfOutlined className="attachment-pdf-icon" />
                  <span>{fileTypeLabel(file)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        open={!!previewFile}
        onCancel={() => setPreviewFile(null)}
        footer={null}
        width={900}
        title={previewFile?.file_name}
        destroyOnClose
      >
        {previewFile && (
          <iframe
            src={previewFile.file_url}
            title={previewFile.file_name}
            style={{ width: "100%", height: "75vh", border: "none" }}
          />
        )}
      </Modal>

      <div className="compose-footer">
        <button type="button" className="compose-cancel" onClick={goBack}>
          Back
        </button>
        <button
          type="button"
          className="compose-send"
          onClick={() => navigate(`/messages/${isSent ? "forward" : "reply"}/${messageId}`)}
        >
          {isSent ? "Forward" : "Reply"}
        </button>
      </div>
    </ComposeStyle>
  );
};

export default ViewMessage;
